import React, { useMemo, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import ConfirmationModal from '../../Components/ConfirmationModal';
import { Stairwell } from '../../types/stairwells';
import { Unit } from '../../types/units';
import { STAIRWELL_NAME_MAX_LENGTH } from '../../Helpers/stairwellUtils';

interface StairwellsSectionProps {
    /** Natural order by name. */
    stairwells: Stairwell[];
    /** Every unit of the building, for the per-stairwell count and the delete confirmation. */
    units: Unit[];
    isAdmin: boolean;
    submitting: boolean;
    onCreate: (name: string) => Promise<unknown>;
    onRename: (stairwellKey: string, name: string) => Promise<void>;
    onDelete: (stairwellKey: string) => Promise<void>;
}

/** Name being typed: a new stairwell (`key` null) or a rename. */
type Editing = { key: string | null; name: string };

/**
 * Trappuppgångar of the building on the units page: name and unit count, with
 * Ny, rename and delete for admins. Stairwells are also created from the unit
 * form. Errors surface through the hook's error on the page.
 */
const StairwellsSection: React.FC<StairwellsSectionProps> = ({ stairwells, units, isAdmin, submitting, onCreate, onRename, onDelete }) => {
    const [editing, setEditing] = useState<Editing | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<Stairwell | null>(null);

    const unitCounts = useMemo(() => {
        const counts: { [key: string]: number } = {};
        for (const unit of units) {
            if (unit.stairwellKey) counts[unit.stairwellKey] = (counts[unit.stairwellKey] ?? 0) + 1;
        }
        return counts;
    }, [units]);

    const save = async () => {
        if (!editing) return;
        try {
            if (editing.key) {
                await onRename(editing.key, editing.name);
            } else {
                await onCreate(editing.name);
            }
            setEditing(null);
        } catch {
            // Shown by the page through the hook's error; the field stays open to correct.
        }
    };

    const remove = async () => {
        if (!confirmDelete) return;
        const key = confirmDelete.key;
        setConfirmDelete(null);
        await onDelete(key).catch(() => undefined);
    };

    const nameField = (
        <Form
            className="units-stairwell-edit"
            onSubmit={e => { e.preventDefault(); void save(); }}
        >
            <Form.Control
                size="sm"
                type="text"
                value={editing?.name ?? ''}
                onChange={e => setEditing(current => (current ? { ...current, name: e.target.value } : current))}
                placeholder="T.ex. A eller Storgatan 3A"
                maxLength={STAIRWELL_NAME_MAX_LENGTH}
                aria-label="Namn på trappuppgången"
                autoFocus
            />
            <Button type="submit" size="sm" variant="primary" disabled={submitting || !editing?.name.trim()} aria-label="Spara">
                <Check size={14} />
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={() => setEditing(null)} disabled={submitting} aria-label="Avbryt">
                <X size={14} />
            </Button>
        </Form>
    );

    const confirmCount = confirmDelete ? unitCounts[confirmDelete.key] ?? 0 : 0;

    return (
        <div>
            <div className="units-section-header">
                <h4>Trappuppgångar</h4>
                {isAdmin && editing?.key !== null && (
                    <Button variant="outline-primary" size="sm" onClick={() => setEditing({ key: null, name: '' })} disabled={submitting}>
                        <Plus size={14} className="me-1" />Ny
                    </Button>
                )}
            </div>
            <ul className="units-stairwell-list">
                {stairwells.map(stairwell => (
                    <li key={stairwell.key} className="units-stairwell-row">
                        {editing?.key === stairwell.key ? nameField : (
                            <>
                                <span className="units-stairwell-name">{stairwell.name}</span>
                                <span className="units-unit-meta">{unitCounts[stairwell.key] ?? 0} enheter</span>
                                {isAdmin && (
                                    <span className="units-stairwell-actions">
                                        <Button
                                            variant="link"
                                            size="sm"
                                            onClick={() => setEditing({ key: stairwell.key, name: stairwell.name })}
                                            disabled={submitting}
                                            aria-label={`Byt namn på ${stairwell.name}`}
                                            title="Byt namn"
                                        >
                                            <Pencil size={14} />
                                        </Button>
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="text-danger"
                                            onClick={() => setConfirmDelete(stairwell)}
                                            disabled={submitting}
                                            aria-label={`Ta bort ${stairwell.name}`}
                                            title="Ta bort"
                                        >
                                            <Trash2 size={14} />
                                        </Button>
                                    </span>
                                )}
                            </>
                        )}
                    </li>
                ))}
                {editing?.key === null && <li className="units-stairwell-row">{nameField}</li>}
            </ul>

            <ConfirmationModal
                show={confirmDelete !== null}
                title="Ta bort trappuppgång"
                message={confirmCount > 0
                    ? `${confirmDelete?.name} tas bort och ${confirmCount === 1 ? '1 enhet' : `${confirmCount} enheter`} blir utan trappuppgång.`
                    : `${confirmDelete?.name} tas bort.`}
                onConfirm={() => void remove()}
                onCancel={() => setConfirmDelete(null)}
            />
        </div>
    );
};

export default StairwellsSection;
